/**
 * StepathonEventPage — individual event dashboard at `/stepathon/:slug`.
 * Shows: live event counter, milestone tier ladder with the participant's
 * current position, leaderboard, browser pedometer + manual log form.
 *
 * Requires login for the Walk Now / Manual Log actions (matches user spec:
 * "must have a glpflo account but you don't need to purchase an order").
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import LiveStepCounter from "../components/LiveStepCounter";
import BrowserPedometer from "../components/BrowserPedometer";
import BadgeShareCard from "../components/BadgeShareCard";
import { Gift } from "lucide-react";

function TierLadder({ tiers, myTotal }) {
  return (
    <div data-testid="tier-ladder" className="grid sm:grid-cols-2 gap-3">
      {tiers.map((t, i) => {
        const reached = myTotal >= t.threshold;
        return (
          <div
            key={i}
            data-testid={`tier-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-2xl border p-4 flex items-center gap-3 transition ${
              reached
                ? "border-pink-400 bg-pink-500/15"
                : "border-purple-200/30 bg-white/5"
            }`}
          >
            <div className="text-3xl">{t.emoji || "🎯"}</div>
            <div className="flex-1">
              <div className={`font-extrabold ${reached ? "text-pink-200" : "text-white"}`}>
                {t.label}
              </div>
              <div className="text-xs text-purple-100/80">
                {t.threshold.toLocaleString()} steps
              </div>
            </div>
            {reached && (
              <span className="text-xs tracking-widest uppercase font-bold text-pink-300">
                Reached
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function StepathonEventPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isAuthed = !!user;
  const [ev, setEv] = useState(null);
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    const evR = await API.get(`/stepathon/events/${slug}`);
    setEv(evR.data);
    const lbR = await API.get(`/stepathon/events/${slug}/leaderboard?limit=15`);
    setBoard(lbR.data.leaderboard || []);
    if (isAuthed) {
      try {
        const meR = await API.get(`/stepathon/events/${slug}/me`);
        setMe(meR.data);
      } catch { setMe(null); }
    } else {
      setMe(null);
    }
  }, [slug, isAuthed]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const id = setInterval(reload, 45_000);
    return () => clearInterval(id);
  }, [reload]);

  const submit = async (payload) => {
    if (!isAuthed) {
      toast.error("Log in first to track steps");
      return;
    }
    setLoading(true);
    try {
      const r = await API.post(
        `/stepathon/events/${slug}/log`,
        payload
      );
      toast.success(`Logged ${payload.steps.toLocaleString()} steps · `
                    + `total: ${r.data.user_total.toLocaleString()}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log steps");
    } finally { setLoading(false); }
  };

  // Note: manual step entry retired (SEC-002 hardening — see stepathon.py
  // Pydantic regex which now rejects source='manual'). Steps come from the
  // BrowserPedometer above or the Companion App auto-tracker only.

  if (!ev) return (
    <div className="min-h-screen bg-[#0a0612] text-white p-8">Loading…</div>
  );

  const goalPct = ev.percent_to_goal || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1B0B3A] via-[#150728] to-[#0a0612] text-white">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <Link to="/stepathon" className="text-sm text-purple-200/80 hover:text-pink-300">
          ← all step-a-thons
        </Link>
        <h1 data-testid="event-name" className="mt-4 text-4xl sm:text-5xl font-black">
          {ev.name}
        </h1>
        <p className="mt-3 text-purple-100/85 max-w-3xl">{ev.description}</p>

        {/* Event live counter */}
        <div className="mt-10 rounded-3xl border border-purple-300/30 bg-purple-900/30 p-8 backdrop-blur">
          <LiveStepCounter
            url={`/api/stepathon/events/${slug}`}
            label={`${ev.name} · live total`}
            sublabel={`Goal: ${ev.goal_steps.toLocaleString()} steps`}
            variant="hero"
          />
          <div className="mt-6 h-3 rounded-full bg-purple-900/50 overflow-hidden">
            <div
              data-testid="event-goal-bar"
              className="h-full bg-gradient-to-r from-purple-400 via-pink-400 to-fuchsia-400 transition-all duration-700"
              style={{ width: `${Math.min(100, goalPct)}%` }}
            />
          </div>
          <p className="mt-2 text-center text-xs text-purple-200/80">
            {goalPct.toFixed(2)}% of goal · {ev.participant_count.toLocaleString()} walkers
          </p>
        </div>

        {/* My progress + log */}
        <section className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-purple-300/30 bg-white/5 p-6">
            <h3 className="text-sm tracking-widest uppercase text-pink-300 font-bold">Your progress</h3>
            {!isAuthed ? (
              <p className="mt-4 text-purple-100/80">
                <Link to="/login" className="underline text-pink-300">Log in</Link>{" "}
                to track your personal steps and tier.
              </p>
            ) : me ? (
              <>
                <div className="mt-3 text-4xl font-black tabular-nums">
                  {me.my_steps.toLocaleString()}
                </div>
                <div className="text-sm text-purple-100/80">
                  steps · {me.my_miles.toFixed(2)} miles
                </div>
                {me.current_tier && (
                  <p className="mt-3 text-sm text-pink-200">
                    Current tier: <b>{me.current_tier.emoji} {me.current_tier.label}</b>
                  </p>
                )}
                {me.next_tier && (
                  <p className="text-sm text-purple-100/80">
                    Next: <b>{me.next_tier.emoji} {me.next_tier.label}</b> —{" "}
                    {me.steps_to_next.toLocaleString()} steps to go
                  </p>
                )}
              </>
            ) : (
              <p className="mt-4 text-purple-100/70">No entries yet — start walking ↓</p>
            )}
          </div>

          <div className="rounded-3xl border border-purple-300/30 bg-white/5 p-6 space-y-4">
            <BrowserPedometer onSubmit={submit} disabled={loading || !isAuthed} />
            <div className="border-t border-purple-200/20 pt-4">
              <p className="text-xs uppercase tracking-widest text-purple-200/80 mb-2">
                Auto-sync from Apple Health / Google Fit / Fitbit
              </p>
              <p className="text-xs text-purple-100/60 leading-relaxed">
                Manual step entry has been retired to protect the rewards ledger. Steps are counted
                automatically by the browser pedometer above (while this page or the Companion App is
                open) and, coming soon, by direct sync from your fitness tracker.
              </p>
            </div>
          </div>
        </section>

        {/* Tier ladder */}
        {ev.tiers && ev.tiers.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-extrabold mb-4">Milestone tiers</h2>
            <TierLadder tiers={ev.tiers} myTotal={me?.my_steps || 0} />
          </section>
        )}

        {/* Shareable badge + Rewards CTA */}
        {isAuthed && me && (
          <section className="mt-10 grid md:grid-cols-2 gap-6">
            <BadgeShareCard
              slug={slug}
              eventName={ev.name}
              tier={me.current_tier}
              totalSteps={me.my_steps}
            />
            <Link
              to="/stepathon/rewards"
              data-testid="rewards-cta"
              className="rounded-3xl border border-purple-300/30 bg-gradient-to-br from-pink-600/30 via-fuchsia-700/30 to-purple-700/40 p-6 hover:border-pink-400/60 transition group"
            >
              <div className="inline-flex items-center gap-2 text-pink-300 text-xs uppercase tracking-widest font-bold">
                <Gift className="w-3.5 h-3.5" /> Step-points · 1 step = 1 point
              </div>
              <div className="mt-3 text-3xl font-black tabular-nums bg-gradient-to-r from-yellow-200 via-pink-200 to-fuchsia-200 bg-clip-text text-transparent">
                {(me?.my_steps || 0).toLocaleString()} pts earned
              </div>
              <p className="mt-2 text-sm text-purple-100/85">
                Spend your steps on GLPFlo coupons, free refills, and limited-edition
                merch curated by HQ. Tap to open your rewards locker →
              </p>
              <div className="mt-4 inline-flex items-center text-pink-200 text-sm font-bold group-hover:translate-x-1 transition">
                Open Rewards →
              </div>
            </Link>
          </section>
        )}

        {/* Leaderboard */}
        <section className="mt-10">
          <h2 className="text-2xl font-extrabold mb-4">Leaderboard · top 15</h2>
          <div data-testid="leaderboard" className="rounded-3xl border border-purple-300/30 bg-white/5 overflow-hidden">
            {board.length === 0 && (
              <p className="p-6 text-purple-100/70 text-sm">Nobody on the board yet. Be the first.</p>
            )}
            {board.map((row) => (
              <div key={row.rank}
                   className="flex items-center gap-4 px-5 py-3 border-b border-purple-200/10 last:border-0">
                <div className="text-purple-200/70 font-bold w-8 text-sm">#{row.rank}</div>
                <div className="flex-1 font-semibold">{row.name}</div>
                {row.tier && (
                  <span className="text-xs px-2 py-1 rounded-full bg-pink-500/20 text-pink-200">
                    {row.tier.emoji} {row.tier.label}
                  </span>
                )}
                <div className="tabular-nums text-right">
                  <div className="font-bold">{row.steps.toLocaleString()}</div>
                  <div className="text-xs text-purple-200/70">{row.miles} mi</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
